import React, { useState } from 'react';
import { Lock, Mail, X, ArrowLeft, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import './ForgotPasswordForm.css';

const ForgotPasswordForm = ({ onClose, switchToLogin }) => {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'reset'
  const [otp, setOtp] = useState(['', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // API base URL - adjust to your environment
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/forgotpass/forgot-password`, { email });
      setSuccessMessage(response.data.message);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    // Only allow numeric values
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus next input
    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const otpCode = otp.join('');
    
    try {
      const response = await axios.post(`${API_URL}/forgotpass/verify-otp`, { 
        email, 
        otp: otpCode 
      });
      
      setResetToken(response.data.resetToken);
      setSuccessMessage(response.data.message);
      setStep('reset');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match!");
      return;
    }
    
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/forgotpass/reset-password`, {
        email,
        resetToken,
        newPassword
      });
      
      setSuccessMessage('Password reset successful! Redirecting to login...');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        switchToLogin();
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Message component to display errors and success messages
  const MessageDisplay = ({ error, success }) => {
    if (!error && !success) return null;
    
    return (
      <div className={`message ${error ? 'error' : 'success'}`}>
        {error && <AlertTriangle size={16} />}
        {error || success}
      </div>
    );
  };

  const renderEmailStep = () => (
    <form onSubmit={handleSendOTP} className="login-form">
      <div className="login-header">
        <Lock size={40} className="login-icon" />
        <h2>Forgot Password</h2>
        <p>Enter your email to reset your password</p>
      </div>

      <MessageDisplay error={error} success={successMessage} />

      <div className="form-group">
        <label htmlFor="email">Email Address</label>
        <div className="input-wrapper">
          <Mail className="input-icon" size={20} />
          <input 
            type="email" 
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            disabled={loading}
          />
        </div>
      </div>

      <button type="submit" className="login-submit" disabled={loading}>
        {loading ? 'Sending...' : 'Send OTP'}
      </button>

      <div className="signup-link">
        <a href="#" onClick={switchToLogin} className="back-to-login">
          <ArrowLeft size={16} /> Back to Login
        </a>
      </div>
    </form>
  );

  const renderOTPStep = () => (
    <form onSubmit={handleVerifyOTP} className="login-form">
      <div className="login-header">
        <Lock size={40} className="login-icon" />
        <h2>Verify OTP</h2>
        <p>Enter the 4-digit code sent to {email}</p>
      </div>

      <MessageDisplay error={error} success={successMessage} />

      <div className="otp-container">
        {otp.map((digit, index) => (
          <input 
            key={index}
            id={`otp-${index}`}
            type="text" 
            maxLength="1"
            value={digit}
            onChange={(e) => handleOTPChange(index, e.target.value)}
            className="otp-input"
            pattern="\d*"
            inputMode="numeric"
            required
            disabled={loading}
          />
        ))}
      </div>

      <div className="otp-resend">
        Didn't receive the code? 
        <a href="#" onClick={handleSendOTP} className={loading ? 'disabled' : ''}>
          Resend OTP
        </a>
      </div>

      <button type="submit" className="login-submit" disabled={loading}>
        {loading ? 'Verifying...' : 'Verify OTP'}
      </button>

      <div className="signup-link">
        <a href="#" onClick={() => setStep('email')} className="back-to-login">
          <ArrowLeft size={16} /> Change Email
        </a>
      </div>
    </form>
  );

  const renderResetStep = () => (
    <form onSubmit={handleResetPassword} className="login-form">
      <div className="login-header">
        <Lock size={40} className="login-icon" />
        <h2>Reset Password</h2>
        <p>Create a new password for your account</p>
      </div>

      <MessageDisplay error={error} success={successMessage} />

      <div className="form-group">
        <label htmlFor="newPassword">New Password</label>
        <div className="input-wrapper">
          <Lock className="input-icon" size={20} />
          <input 
            type="password"
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            required
            minLength={8}
            disabled={loading}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="confirmPassword">Confirm Password</label>
        <div className="input-wrapper">
          <Lock className="input-icon" size={20} />
          <input 
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            required
            minLength={8}
            disabled={loading}
          />
        </div>
      </div>

      <button type="submit" className="login-submit" disabled={loading}>
        {loading ? 'Resetting...' : 'Reset Password'}
      </button>

      <div className="signup-link">
        <a href="#" onClick={switchToLogin} className="back-to-login">
          <ArrowLeft size={16} /> Back to Login
        </a>
      </div>
    </form>
  );

  return (
    <div className="login-overlay">
      <div className="login-modal">
        <button className="close-btn" onClick={onClose}>
          <X size={24} />
        </button>
        
        <div className="login-content">
          {step === 'email' && renderEmailStep()}
          {step === 'otp' && renderOTPStep()}
          {step === 'reset' && renderResetStep()}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;